import { EventEmitter, Input, OnInit, Output, Component } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { AcuityShiftRecordService } from 'src/shared/services/shift-record-acuity.service';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { AncillaryConfiguration } from 'src/shared/models/ancillary-configuration.model';
import { RoomAcuityRecord } from 'src/shared/models/room-acuity-record.model';
import { Room } from 'src/shared/models/room.model';
import { AcuityRoomsUtility } from '../acuity-rooms-utility.service';
import { RecommendationService } from 'src/shared/services/recommendation.service';
import { PageInterface } from '../../planner/page/page.interface';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { JobTitleWithTargetDifference } from 'src/shared/models/job-title-with-target-difference.model';
import { debounceTime } from 'rxjs/operators';
import { AncillaryEnterCurrentBaseComponent } from '../../base-classes/ancillary-enter-current-base.component';
import { AcuityCensusRecord } from 'src/shared/models/acuity-census-record.model';
import { AcuityCategory } from 'src/shared/models/acuity-category.model';
import { Constants } from 'src/shared/utilities/constants.util';

@Component({
  selector: 'smt-enter-current-rooms',
  templateUrl: './enter-current-rooms.component.html',
  styleUrls: ['./enter-current-rooms.component.scss'],
})
export class EnterCurrentRoomsComponent extends AncillaryEnterCurrentBaseComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public shiftRecord: ShiftRecord;
    public formGroups: UntypedFormGroup[];
    public acuityCensusRecords: AcuityCensusRecord[];

    public jobTitlesWithVariance: JobTitleWithTargetDifference[];
    public prevShiftRecord: ShiftRecord;
    public currentBlockForm:UntypedFormGroup;
    public currentBlockFormValues$;
    public acuityCategories: AcuityCategory[];
    loading: boolean;

    get ancillaryConfiguration(): AncillaryConfiguration {
        return this.shiftRecord.costCenter.ancillaryConfiguration;
    }

    constructor(
        protected _workflowUtilityService: WorkflowUtilityService,
        protected _acuityShiftRecordService: AcuityShiftRecordService,
        protected _router: Router,
        protected _acuityRoomsService: AcuityRoomsUtility,
        protected _recommendationService: RecommendationService) { 
            super(_recommendationService, _router, _workflowUtilityService);
        }
        
    async ngOnInit() {
        super.initialize(this.page, () => this.initialize());
    }

    async initialize() {
        this.initializeStaffForm();
        this.initializeCurrentBlockForm();
        this.formGroups = [ this.currentBlockForm, this.staffForm];
        this.acuityCategories= this._workflowUtilityService.buildAcuityCategories(this.shiftRecord);      
    }

    isFormValid(): boolean {
        return this.staffForm && this.staffForm.valid && this.currentBlockForm && this.currentBlockForm.valid && this.isValidStaffControls();
    }

    controlValueOutOfRange(staffFormId) : boolean {
        let control = this.staffForm.get(this.getStaffControlName(staffFormId));
        return this._workflowUtilityService.valueOutOfRange(control,this.minValue,this.maxValue);
    }
    
    save() {
        this.shiftRecord.acuityCensusRecords = this.acuityCensusRecords;

        this._acuityRoomsService.setRoomAcuities(this.ancillaryConfiguration, this.shiftRecord, this.currentBlockForm);
        
        this.setStaffPlans();
        this.setStatus();
        return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    shouldSkipSave(): boolean {
        return false;
    }

    getRoomAcuity(room: Room) : RoomAcuityRecord {
        return this._acuityRoomsService.getRoomAcuity(this.shiftRecord, room);
    }
  
    setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.EnterReasons;
        }
    }
    
    private async initializeCurrentBlockForm() {
        this.currentBlockForm = new UntypedFormGroup({});

        if (!this.ancillaryConfiguration.roomTypes) {
            return;
        }

        this.ancillaryConfiguration.roomTypes.forEach(roomType => {
            roomType.rooms.forEach(room => {
                this.currentBlockForm.addControl(this._acuityRoomsService.getRoomControlName(room.id), new UntypedFormControl(null, Validators.required));
            });            
        });

        this.currentBlockFormValues$ = this.currentBlockForm.valueChanges.pipe(
            debounceTime(Constants.DebounceTime)
        ).subscribe(async values => {
            let acuityCount =  this._acuityRoomsService.updateAcuityTotal(values, this.ancillaryConfiguration);
            this.acuityCensusRecords = this._acuityRoomsService.createAcuityCensusRecords(acuityCount, this.shiftRecord);
            this.updateStaffingRecommendation(this._recommendationService.makeAcuityRecommendation(this.shiftRecord.shiftBlockId, this.shiftRecord.workflowTimeFacility, this.acuityCensusRecords));
        });

        if (this.ancillaryConfiguration.defaultToNoOccupancy) {
            this.setAllRoomsAsVacant();
        }

        this._acuityRoomsService.populateRoomAcuityValues(this.ancillaryConfiguration, this.shiftRecord, this.currentBlockForm);
    }

    private setAllRoomsAsVacant() {         
        this.ancillaryConfiguration.roomTypes.forEach(roomType => {
            roomType.rooms.forEach(room => {
                let roomAcuityRecord = this._acuityRoomsService.getRoomAcuity(this.shiftRecord, room);
                if (!roomAcuityRecord.acuityCategoryId || roomAcuityRecord.acuityCategoryId == null) {
                    roomAcuityRecord.isVacant = true;
                }
            });            
        });
    }
}
